import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import "@/App.css";

import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "@/context/BookingContext";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { ScrollProgress } from "@/components/site/ScrollProgress";
import { ErrorBoundary } from "@/components/site/ErrorBoundary";
import { DecisionSave } from "@/components/site/DecisionSave";
import { AnalyticsTracker } from "@/components/site/AnalyticsTracker";

const Home=lazy(()=>import("@/pages/Home"));
const About=lazy(()=>import("@/pages/About"));
const Projects=lazy(()=>import("@/pages/Projects"));
const ProjectDetail=lazy(()=>import("@/pages/ProjectDetail"));
const Amenities=lazy(()=>import("@/pages/Amenities"));
const FloorPlans=lazy(()=>import("@/pages/FloorPlans"));
const ConstructionUpdates=lazy(()=>import("@/pages/ConstructionUpdates"));
const Gallery=lazy(()=>import("@/pages/Gallery"));
const ChannelPartner=lazy(()=>import("@/pages/ChannelPartner"));
const Contact=lazy(()=>import("@/pages/Contact"));
const Blog=lazy(()=>import("@/pages/Blog"));
const BlogPost=lazy(()=>import("@/pages/BlogPost"));
const FAQ=lazy(()=>import("@/pages/FAQ"));
const Legal=lazy(()=>import("@/pages/Legal"));
const Careers=lazy(()=>import("@/pages/Careers"));
const Compare=lazy(()=>import("@/pages/Compare"));
const EmiCalculator=lazy(()=>import("@/pages/EmiCalculator"));
const AdminLogin=lazy(()=>import("@/pages/admin/AdminLogin"));
const AdminLayout=lazy(()=>import("@/pages/admin/AdminLayout"));
const Dashboard=lazy(()=>import("@/pages/admin/Dashboard"));
const LeadsAdmin=lazy(()=>import("@/pages/admin/LeadsAdmin"));
const BlogAdmin=lazy(()=>import("@/pages/admin/BlogAdmin"));
const ProjectsAdmin=lazy(()=>import("@/pages/admin/ProjectsAdmin"));
const InventoryAdmin=lazy(()=>import("@/pages/admin/InventoryAdmin"));
const MediaAdmin=lazy(()=>import("@/pages/admin/MediaAdmin"));
const ConstructionAdmin=lazy(()=>import("@/pages/admin/ConstructionAdmin"));
const StaffAdmin=lazy(()=>import("@/pages/admin/StaffAdmin"));
const ActivityAdmin=lazy(()=>import("@/pages/admin/ActivityAdmin"));
const CrmKanban=lazy(()=>import("@/pages/admin/CrmKanban"));
const VisitCalendarAdmin=lazy(()=>import("@/pages/admin/VisitCalendarAdmin"));
const CommunicationsAdmin=lazy(()=>import("@/pages/admin/CommunicationsAdmin"));
const PartnersAdmin=lazy(()=>import("@/pages/admin/PartnersAdmin"));
const CommissionsAdmin=lazy(()=>import("@/pages/admin/CommissionsAdmin"));
const BuyerLogin=lazy(()=>import("@/pages/buyer/BuyerLogin"));
const BuyerDashboard=lazy(()=>import("@/pages/buyer/BuyerDashboard"));
const PartnerRegister=lazy(()=>import("@/pages/partner/PartnerRegister"));
const PartnerLogin=lazy(()=>import("@/pages/partner/PartnerLogin"));
const PartnerDashboard=lazy(()=>import("@/pages/partner/PartnerDashboard"));
const VisitManage=lazy(()=>import("@/pages/VisitManage"));
const AnalyticsAdmin=lazy(()=>import("@/pages/admin/AnalyticsAdmin"));
const PropertyAdvisor=lazy(()=>import("@/pages/PropertyAdvisor"));

function RouteFallback(){return <div className="flex min-h-screen items-center justify-center bg-ink" role="status" aria-live="polite"><div className="w-full max-w-sm px-6"><div className="h-2 animate-pulse rounded bg-gold/50"/><p className="mt-4 text-center text-xs uppercase tracking-[.25em] text-white/45">Preparing your experience…</p></div></div>}

function SiteLayout() {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="grain min-h-screen bg-ink">
        <ScrollProgress />
        <AnalyticsTracker />
        <Navbar />
        <main><Outlet /></main>
        <Footer />
        <StickyMobileCTA />
        <DecisionSave />
        <div className="h-16 md:hidden" />
      </div>
    </ReactLenis>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <BookingProvider>
            <Suspense fallback={<RouteFallback/>}><Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />
                <Route path="/amenities" element={<Amenities />} />
                <Route path="/floor-plans" element={<FloorPlans />} />
                <Route path="/construction-updates" element={<ConstructionUpdates />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/channel-partner" element={<ChannelPartner />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/emi" element={<EmiCalculator />} />
                <Route path="/advisor" element={<PropertyAdvisor />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/buyer/login" element={<BuyerLogin />} />
              <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
              <Route path="/partner/register" element={<PartnerRegister />} />
              <Route path="/partner/login" element={<PartnerLogin />} />
              <Route path="/partner/dashboard" element={<PartnerDashboard />} />
              <Route path="/visit/manage" element={<VisitManage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="leads" element={<LeadsAdmin />} />
                <Route path="blog" element={<BlogAdmin />} />
                <Route path="projects" element={<ProjectsAdmin />} />
                <Route path="inventory" element={<InventoryAdmin />} />
                <Route path="media" element={<MediaAdmin />} />
                <Route path="construction" element={<ConstructionAdmin />} />
                <Route path="staff" element={<StaffAdmin />} />
                <Route path="activity" element={<ActivityAdmin />} />
                <Route path="crm" element={<CrmKanban />} />
                <Route path="visits" element={<VisitCalendarAdmin />} />
                <Route path="communications" element={<CommunicationsAdmin />} />
                <Route path="partners" element={<PartnersAdmin />} />
                <Route path="commissions" element={<CommissionsAdmin />} />
                <Route path="analytics" element={<AnalyticsAdmin />} />
              </Route>
            </Routes></Suspense>
          </BookingProvider>
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
